import { gql } from '@apollo/client'

// Delivery routes ("runs") — master contract: API/docs/frontend/pos-delivery-run-contract.md.
//
// Kept in their OWN documents on purpose, NOT added to the board's recentOrders, the
// driver's driverDeliveries or the order detail's GetOrder: an unknown field fails the
// whole operation, so those screens keep loading against an API that has not deployed
// routes yet, and only the route surfaces go dark until it has.
//
// Stops are normalized `Order` objects, so a route mutation's result also refreshes the
// same orders wherever else they are cached (board lanes, map).

export const ROUTE_STOP_FIELDS = gql`
  fragment RouteStop on Order {
    id
    order_number
    status
    order_type
    order_source
    platform
    delivered_by
    customer_name
    customer_phone
    customer_phone_access_code
    delivery_street
    delivery_house_number
    delivery_postcode
    delivery_city
    delivery_instructions
    delivery_lat
    delivery_lng
    delivery_geo_precision
    scheduled_for
    is_preorder
    created_at
    current_status_since
    delivery_run_position
    delivery_eta_at
    delivery_stops_before
    delivered_by_user {
      id
      name
    }
  }
`

export const ROUTE_FIELDS = gql`
  ${ROUTE_STOP_FIELDS}
  fragment Route on DeliveryRunDetail {
    id
    status
    started_at
    completed_at
    expected_back_at
    stops_total
    stops_delivered
    driver {
      id
      name
    }
    stops {
      ...RouteStop
    }
  }
`

// Operators' board: PLANNED + ACTIVE (+ COMPLETED of the last 24h when asked). delivery.plan.
export const GET_DELIVERY_RUNS = gql`
  ${ROUTE_FIELDS}
  query DeliveryRuns($includeCompleted: Boolean) {
    deliveryRuns(include_completed: $includeCompleted) {
      ...Route
    }
  }
`

// The calling driver's own routes: PLANNED, ACTIVE, COMPLETED of the last 24h. delivery.drive.
export const GET_MY_DELIVERY_RUNS = gql`
  ${ROUTE_FIELDS}
  query MyDeliveryRuns {
    myDeliveryRuns {
      ...Route
    }
  }
`

// Assign-driver picker: active users holding delivery.drive. delivery.plan.
export const GET_DELIVERY_DRIVERS = gql`
  query DeliveryDrivers {
    deliveryDrivers {
      id
      name
    }
  }
`

// Route facts for the order detail page — the same fields the customer's tracking page
// shows. Separate from GetOrder for the deploy-safety reason above.
export const GET_ORDER_DELIVERY_RUN = gql`
  query GetOrderDeliveryRun($id: Int!) {
    order(id: $id) {
      id
      delivery_run {
        id
        status
        started_at
        completed_at
        stops_total
        stops_delivered
        driver {
          id
          name
        }
      }
      delivery_run_position
      delivery_eta_at
      delivery_stops_before
    }
  }
`

// --- Routes driven (Delivery Reports; stats.delivery_runs.view — owner and admin) ---------

// The stop as the history shows it: the board's stop plus the two real moments. A separate
// fragment on purpose, NOT added to RouteStop: ready_at / delivered_at are unknown fields on an
// API without the 2026-10-02 change, and adding them to the Route fragment would take the
// board and the driver screen down with this report.
export const ROUTE_STOP_DRIVEN_FIELDS = gql`
  ${ROUTE_STOP_FIELDS}
  fragment RouteStopDriven on Order {
    ...RouteStop
    ready_at
    delivered_at
  }
`

// COMPLETED routes whose started_at falls in [from, to] (local-day bounds as UTC ISO), newest
// first, window ≤ 31 days. driver_user_id is deliberately NOT sent: one read per window, and the
// driver filter and every rollup are client-side over the same rows — so the driver picker
// lists exactly the drivers who drove in the window. The argument stays available on the API if
// a window ever gets too heavy.
export const GET_DELIVERY_RUN_HISTORY = gql`
  ${ROUTE_STOP_DRIVEN_FIELDS}
  query DeliveryRunHistory($from: String!, $to: String!) {
    deliveryRunHistory(from: $from, to: $to) {
      id
      status
      started_at
      completed_at
      expected_back_at
      stops_total
      stops_delivered
      driver {
        id
        name
      }
      stops {
        ...RouteStopDriven
      }
    }
  }
`

// --- Planning (delivery.plan) — PLANNED routes only --------------------------------------

export const CREATE_DELIVERY_RUN = gql`
  ${ROUTE_FIELDS}
  mutation CreateDeliveryRun($driverUserId: Int) {
    createDeliveryRun(driver_user_id: $driverUserId) {
      ...Route
    }
  }
`

export const ASSIGN_DELIVERY_RUN_DRIVER = gql`
  ${ROUTE_FIELDS}
  mutation AssignDeliveryRunDriver($id: Int!, $driverUserId: Int) {
    assignDeliveryRunDriver(id: $id, driver_user_id: $driverUserId) {
      ...Route
    }
  }
`

// Replaces the WHOLE stop list, in delivery sequence: one call covers add, remove and
// reorder. [] empties the route. At most 6.
export const SET_DELIVERY_RUN_STOPS = gql`
  ${ROUTE_FIELDS}
  mutation SetDeliveryRunStops($id: Int!, $orderIds: [Int!]!) {
    setDeliveryRunStops(id: $id, order_ids: $orderIds) {
      ...Route
    }
  }
`

export const DELETE_DELIVERY_RUN = gql`
  mutation DeleteDeliveryRun($id: Int!) {
    deleteDeliveryRun(id: $id)
  }
`

// --- Driving (delivery.drive) — the ASSIGNED driver only ---------------------------------

export const START_DELIVERY_RUN = gql`
  ${ROUTE_FIELDS}
  mutation StartDeliveryRun($id: Int!) {
    startDeliveryRun(id: $id) {
      ...Route
    }
  }
`

export const UNDO_START_DELIVERY_RUN = gql`
  ${ROUTE_FIELDS}
  mutation UndoStartDeliveryRun($id: Int!) {
    undoStartDeliveryRun(id: $id) {
      ...Route
    }
  }
`

// order_ids = exactly the stops still IN_DELIVERY, in the new order.
export const REORDER_DELIVERY_RUN = gql`
  ${ROUTE_FIELDS}
  mutation ReorderDeliveryRun($id: Int!, $orderIds: [Int!]!) {
    reorderDeliveryRun(id: $id, order_ids: $orderIds) {
      ...Route
    }
  }
`
